import React, { FC, useState } from 'react';
import Box from '@mui/material/Box';

import TwDialog from '../TwDialog/TwDialog';
import { ReactComponent as Warning } from '../../assets/icons/warning.svg';
import { TwButton, TwButtonType, TwLoadingButton } from '../TwButtons/TwButtons';
import { logRocketTrack } from '../../utils/logRocketUtils';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import useAuth from '../../providers/Auth/Auth.provider';
import { useDeleteInvoiceMutation } from '../../redux/api/orders/ordersApi';

interface TwDeleteInvoiceProps {
  open: boolean;
  invoiceId: string;
  message?: string;
  onCancel: () => void;
  onDelete?: () => void;
}

const DEFAULT_MESSAGE = 'Are you sure you want to delete this Invoice? You can’t undo this action.';

const TwDeleteInvoice: FC<TwDeleteInvoiceProps> = ({
  invoiceId,
  open,
  message,
  onCancel,
  onDelete
}) => {
  const {
    data: { userData }
  } = useAuth();
  const { addNotification } = useNotifications();
  const [deleteInvoice] = useDeleteInvoiceMutation();

  const [loading, setLoading] = useState<boolean>(false);

  const handleDeleteInvoice = async (): Promise<void> => {
    if (!invoiceId) return;

    setLoading(true);
    deleteInvoice(invoiceId)
      .unwrap()
      .then((status) => {
        if (status === 200) {
          onDelete && onDelete();
        }
      })
      .catch(() => {
        logRocketTrack('Invoice', {
          action: 'Delete created or sent invoice',
          severity: 'medium',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        addNotification({
          message: 'Error deleting invoice',
          variant: 'error'
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <TwDialog
      open={open}
      title="Delete invoice?"
      actionsAlignment="flex-end"
      onClickClose={onCancel}
      actions={[
        <TwButton
          key="1"
          type={TwButtonType.secondary}
          label="Cancel"
          action={(): void => {
            onCancel();
          }}
        />,
        <TwLoadingButton
          key="2"
          loading={loading}
          type={TwButtonType.negative}
          label="Delete"
          action={handleDeleteInvoice}
        />
      ]}
      sx={{
        zIndex: '1500 !important'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '20px',
          width: '100%'
        }}
      >
        <Warning style={{ marginBottom: '20px' }} />
        <Box component="p" sx={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
          {message || DEFAULT_MESSAGE}
        </Box>
      </Box>
    </TwDialog>
  );
};

export default TwDeleteInvoice;
